// src/pages/KitchenSchematic.js
//
// Read-only plan-view kitchen schematic rendered from measured wall data.
// Vibrant, illustrative cabinets (decorative) with real measured dimensions.
// Renders responsively (scales to its container) so it also works on mobile.
import React from "react";

const NAVY = "#1e3a5f";
const CAB_LIGHT = "#e8edf3";
const CAB_MID = "#d3dce6";
const CAB_LINE = "#9fb0c3";
const KNOB = "#c9a24b";
const FLOOR = "#f4f1ea";
const FLOOR_LINE = "#e0d8c8";
const ACCENT = "#0057b8";
const GRAYNOTE = "#8a97a8";

const ftLabel = (feet) => {
  if (feet == null || !isFinite(Number(feet))) return "\u2014";
  const t = Math.round(Number(feet) * 12);
  const ft = Math.floor(t / 12);
  const inch = t % 12;
  if (ft === 0 && inch === 0) return "0\u2033";
  if (inch === 0) return `${ft}\u2032`;
  if (ft === 0) return `${inch}\u2033`;
  return `${ft}\u2032 ${inch}\u2033`;
};

function CabinetRun({ x, y, w, h, front }) {
  const els = [];
  const horizontal = w >= h;
  const runLen = horizontal ? w : h;
  const inset = 4;
  const fx = x + inset, fy = y + inset, fw = w - inset * 2, fh = h - inset * 2;
  els.push(<rect key="counter" x={x} y={y} width={w} height={h} rx={2.5} fill="url(#counterGrad)" />);
  els.push(<rect key="face" x={fx} y={fy} width={fw} height={fh} rx={1.5} fill="url(#cabGrad)" />);
  const nSeg = Math.max(1, Math.round(runLen / 42));
  const segLen = (runLen - inset * 2) / nSeg;
  const doorPad = 2.5;
  for (let i = 0; i < nSeg; i++) {
    let dx, dy, dw, dh;
    if (horizontal) { dx = fx + i * segLen + doorPad; dy = fy + doorPad; dw = segLen - doorPad * 2; dh = fh - doorPad * 2; }
    else { dx = fx + doorPad; dy = fy + i * segLen + doorPad; dw = fw - doorPad * 2; dh = segLen - doorPad * 2; }
    els.push(<rect key={`d${i}`} x={dx} y={dy} width={dw} height={dh} rx={1.5} fill={CAB_MID} stroke={CAB_LINE} strokeWidth={0.8} />);
    els.push(<rect key={`di${i}`} x={dx + 2} y={dy + 2} width={Math.max(0, dw - 4)} height={Math.max(0, dh - 4)} rx={1} fill={CAB_LIGHT} />);
    let kx, ky;
    if (horizontal) { kx = dx + dw / 2; ky = front === "top" ? dy + 4 : dy + dh - 4; }
    else { ky = dy + dh / 2; kx = front === "left" ? dx + 4 : dx + dw - 4; }
    els.push(<circle key={`k${i}`} cx={kx} cy={ky} r={2.2} fill={KNOB} stroke="#8a6d2f" strokeWidth={0.5} />);
  }
  return <g>{els}</g>;
}

const SinkIcon = ({ cx, cy }) => (
  <g>
    <rect x={cx - 13} y={cy - 9} width={26} height={18} rx={3} fill="#f4f1ea" stroke={NAVY} strokeWidth={1.2} />
    <rect x={cx - 9} y={cy - 5.5} width={18} height={11} rx={2} fill="#dfe7ef" stroke={NAVY} strokeWidth={0.8} />
    <circle cx={cx} cy={cy} r={1.6} fill={NAVY} />
    <path d={`M ${cx} ${cy - 9} q 0 -5 4 -5`} fill="none" stroke={NAVY} strokeWidth={1.5} />
  </g>
);
const RangeIcon = ({ cx, cy }) => {
  const b = (dx, dy, k) => (
    <g key={k}>
      <circle cx={cx + dx} cy={cy + dy} r={2.8} fill="none" stroke={NAVY} strokeWidth={1.3} />
      <circle cx={cx + dx} cy={cy + dy} r={0.9} fill={NAVY} />
    </g>
  );
  return (
    <g>
      <rect x={cx - 12} y={cy - 12} width={24} height={24} rx={2.5} fill="#f4f1ea" stroke={NAVY} strokeWidth={1.2} />
      {b(-5.5, -5.5, 1)}{b(5.5, -5.5, 2)}{b(-5.5, 5.5, 3)}{b(5.5, 5.5, 4)}
    </g>
  );
};
const DimLabel = ({ cx, cy, text }) => {
  const w = 16 + String(text).length * 9;
  return (
    <g filter="url(#pill)">
      <rect x={cx - w / 2} y={cy - 13} width={w} height={26} rx={13} fill={ACCENT} />
      <text x={cx} y={cy + 5} textAnchor="middle" fontFamily="Arial, sans-serif" fontSize={15} fontWeight={800} fill="#fff">{text}</text>
    </g>
  );
};
const RoleLabel = ({ cx, cy, text }) => (
  <text x={cx} y={cy} textAnchor="middle" fontFamily="Arial, sans-serif" fontSize={10.5} fontWeight={800} fill={NAVY} letterSpacing={1.2}>{text}</text>
);

export default function KitchenSchematic({ layout, walls, projectName, summary }) {
  const W = 720, H = 470, drawTop = 74, pad = 88, DEPTH = 32;
  const len = (r) => walls?.[r]?.lengthFt || 0;
  const content = [];

  const isU = layout === "u-shape";

  if (isU) {
    const back = len("back"), left = len("left"), right = len("right");
    const areaW = W - pad * 2, areaH = H - drawTop - pad;
    const scale = Math.min(areaW / Math.max(back, 1), areaH / Math.max(left, right, 1)) * 0.9;
    const bPx = back * scale, lPx = left * scale, rPx = right * scale;
    const cx = W / 2, bX = cx - bPx / 2, bY = drawTop + 30;
    const floorH = Math.max(lPx, rPx);
    content.push(<rect key="floor" x={bX + DEPTH} y={bY + DEPTH} width={bPx - DEPTH * 2} height={floorH - DEPTH} fill={FLOOR} stroke={FLOOR_LINE} strokeWidth={1.5} />);
    content.push(<g key="runs" filter="url(#ds)">
      <CabinetRun x={bX} y={bY} w={bPx} h={DEPTH} front="bottom" />
      <CabinetRun x={bX} y={bY} w={DEPTH} h={lPx} front="right" />
      <CabinetRun x={bX + bPx - DEPTH} y={bY} w={DEPTH} h={rPx} front="left" />
    </g>);
    content.push(<RangeIcon key="range" cx={cx} cy={bY + DEPTH / 2} />);
    content.push(<SinkIcon key="sink" cx={bX + DEPTH / 2} cy={bY + lPx * 0.55} />);
    content.push(<DimLabel key="dback" cx={cx} cy={bY - 18} text={ftLabel(back)} />);
    content.push(<DimLabel key="dleft" cx={bX - 36} cy={bY + lPx / 2} text={ftLabel(left)} />);
    content.push(<DimLabel key="dright" cx={bX + bPx + 36} cy={bY + rPx / 2} text={ftLabel(right)} />);
    content.push(<RoleLabel key="rback" cx={cx} cy={bY + DEPTH + 42} text="BACK WALL" />);
    content.push(<RoleLabel key="rleft" cx={bX - 36} cy={bY + lPx + 20} text="LEFT" />);
    content.push(<RoleLabel key="rright" cx={bX + bPx + 36} cy={bY + rPx + 20} text="RIGHT" />);
  } else {
    const back = len("back"), side = len("side");
    const areaW = W - pad * 2, areaH = H - drawTop - pad;
    const scale = Math.min(areaW / Math.max(back, 1), areaH / Math.max(side, 1)) * 0.88;
    const bPx = back * scale, sPx = side * scale;
    const oX = pad + 30, oY = drawTop + 34;
    content.push(<rect key="floor" x={oX + DEPTH} y={oY + DEPTH} width={bPx - DEPTH} height={sPx - DEPTH} fill={FLOOR} stroke={FLOOR_LINE} strokeWidth={1.5} />);
    content.push(<g key="runs" filter="url(#ds)">
      <CabinetRun x={oX} y={oY} w={bPx} h={DEPTH} front="bottom" />
      <CabinetRun x={oX} y={oY} w={DEPTH} h={sPx} front="right" />
    </g>);
    content.push(<RangeIcon key="range" cx={oX + bPx * 0.62} cy={oY + DEPTH / 2} />);
    content.push(<SinkIcon key="sink" cx={oX + DEPTH / 2} cy={oY + sPx * 0.6} />);
    content.push(<DimLabel key="dback" cx={oX + bPx / 2} cy={oY - 18} text={ftLabel(back)} />);
    content.push(<DimLabel key="dside" cx={oX - 36} cy={oY + sPx / 2} text={ftLabel(side)} />);
    content.push(<RoleLabel key="rback" cx={oX + bPx / 2} cy={oY + DEPTH + 40} text="BACK WALL" />);
    content.push(<RoleLabel key="rside" cx={oX + DEPTH + 42} cy={oY + sPx + 16} text="SIDE WALL" />);
  }

  const total = summary?.totalLinearFt != null
    ? Number(summary.totalLinearFt)
    : Object.values(walls || {}).reduce((s, w) => s + (w.lengthFt || 0), 0);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", borderRadius: 12, boxShadow: "0 4px 18px rgba(30,58,95,0.15)" }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="counterGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5a6b7d" /><stop offset="1" stopColor="#3d4c5e" />
        </linearGradient>
        <linearGradient id="cabGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#eef2f7" /><stop offset="1" stopColor="#d3dce6" />
        </linearGradient>
        <filter id="ds" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#1e3a5f" floodOpacity="0.28" />
        </filter>
        <filter id="pill" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.6" floodColor="#1e3a5f" floodOpacity="0.4" />
        </filter>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      <rect x="0" y="0" width={W} height={60} fill={NAVY} />
      <text x="36" y="30" fontFamily="Georgia, serif" fontSize={21} fontWeight={700} fill="#fff">{projectName || "Kitchen"}</text>
      <text x="36" y="48" fontFamily="Arial, sans-serif" fontSize={10.5} fontWeight={700} fill="#9fb8d6" letterSpacing={2}>
        {isU ? "U-SHAPE KITCHEN \u00b7 PLAN VIEW" : "L-SHAPE KITCHEN \u00b7 PLAN VIEW"}
      </text>
      {content}
      <g filter="url(#pill)">
        <rect x={W - 206} y={H - 46} width={188} height={32} rx={16} fill={NAVY} />
        <text x={W - 112} y={H - 25} textAnchor="middle" fontFamily="Arial, sans-serif" fontSize={12.5} fontWeight={800} fill="#fff">
          TOTAL RUN: {total.toFixed(1)} ft
        </text>
      </g>
      <text x="24" y={H - 22} fontFamily="Arial, sans-serif" fontSize={10.5} fontStyle="italic" fill={GRAYNOTE}>
        {"Illustrative layout \u00b7 dimensions are estimates for planning"}
      </text>
    </svg>
  );
}
